import debounce from "lodash/debounce";
import type { QueryClient } from "@tanstack/react-query";
import type { MissionControlEventSubscription } from "@/context/MissionControlWebSocketContext";
import { clientsKeys, type ClientTasksKeyInput } from "./keys";
import { createClientMissionsWsSubscription } from "./useClientMissions";
import {
  createClientTasksWsSubscription,
  createDebouncedClientTasksInvalidate,
} from "./useClientTasks";

/** Debounced invalidation for client header counts after task/mission WS events. */
export function createDebouncedClientDetailInvalidate(
  queryClient: QueryClient,
  faClientId: string,
  waitMs = 500,
): { invalidate: () => void; cancel: () => void } {
  const debouncedDetail = debounce(() => {
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "detail", faClientId],
    });
  }, waitMs);
  // Checklist populate often finishes shortly after the monitoring task WS event.
  const debouncedDataGathering = debounce(() => {
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "data-gathering", faClientId],
    });
  }, Math.max(waitMs, 2500));
  return {
    invalidate: () => {
      debouncedDetail();
      debouncedDataGathering();
    },
    cancel: () => {
      debouncedDetail.cancel();
      debouncedDataGathering.cancel();
    },
  };
}

function mergeClientDetailWsHandlers(
  missions: MissionControlEventSubscription,
  tasks: MissionControlEventSubscription,
  onAfterEvent: () => void,
): MissionControlEventSubscription {
  const wrap = <Payload>(
    handler: ((payload: Payload) => void) | undefined,
  ): ((payload: Payload) => void) | undefined => {
    if (!handler) return undefined;
    return (payload: Payload) => {
      handler(payload);
      onAfterEvent();
    };
  };

  return {
    onMissionCreated: wrap(missions.onMissionCreated),
    onMissionStageChanged: wrap(missions.onMissionStageChanged),
    onMissionCompleted: wrap(missions.onMissionCompleted),
    onTaskCreated: wrap(tasks.onTaskCreated),
    onTaskStatusChanged: wrap(tasks.onTaskStatusChanged),
    onTaskNeedsAttention: wrap(tasks.onTaskNeedsAttention),
  };
}

/** Single WS subscription for ClientDetail — patches all client caches while the window is live. */
export function createClientDetailWsSubscription(
  queryClient: QueryClient,
  faClientId: string,
  tasksKeyInput: ClientTasksKeyInput,
  debouncedInvalidateDetail: () => void,
): MissionControlEventSubscription {
  const debouncedTasks = createDebouncedClientTasksInvalidate(queryClient, tasksKeyInput);
  const missions = createClientMissionsWsSubscription(queryClient, faClientId);
  const tasks = createClientTasksWsSubscription(
    queryClient,
    faClientId,
    tasksKeyInput,
    debouncedTasks.invalidate,
  );
  return mergeClientDetailWsHandlers(missions, tasks, debouncedInvalidateDetail);
}
